import type { DateTime } from "luxon";
import type { BaseWage, Nurse, NurseInShift } from "./models";
import { WageType } from "./models";
import { isAgencyNurse } from "./warningDialogs/utils";

/**
 * Resolve whether a worked shift is excluded from PPD/HPPD.
 *
 * With job management on, the per-shift flag (resolved by the backend against the
 * shift's job + dept override) is authoritative. Otherwise we fall back to the
 * nurse-level flag. Each caller resolves its own shift/nurse values however it has
 * them (direct ref, options bag, or a lookup) and passes the booleans in.
 */
export const isShiftExcludedFromPpd = (
	jobManagementEnabled: boolean,
	shiftExcludeFromPpd: boolean | undefined,
	nurseExcludeFromPpd: boolean | undefined,
): boolean =>
	// DEFAULT: an absent flag means "included" — legacy nurses/shifts predate PPD exclusion
	// and keep their historical behavior of counting toward PPD.
	jobManagementEnabled ? !!shiftExcludeFromPpd : !!nurseExcludeFromPpd;

/**
 * Find the BaseWage entry that applies at a given dateTime.
 * Mirrors backend get_wage_by_date in calcaulte_cost_logic.py.
 */
export function getBaseWageByTime(nurse: Nurse, dateTime: DateTime): BaseWage | undefined {
	if (!nurse.baseWages || nurse.baseWages.length === 0) return undefined;

	const utcMillis = dateTime.toUTC().toMillis();

	return nurse.baseWages.find((bw) => {
		if (!bw.wage || bw.wage <= 0) return false;
		const startMillis = bw.startTime.toUTC().toMillis();
		if (startMillis > utcMillis) return false;
		if (bw.endTime) {
			const endMillis = bw.endTime.toUTC().toMillis();
			if (endMillis <= utcMillis) return false;
		}
		return true;
	});
}

/**
 * Whether a nurse is salaried (annually paid) on a given date, based on their base wage for that
 * date (not the deprecated `nurse.wageType`). No base wage for the date → treated as not salaried.
 * Used to keep salaried staff out of shift delta, since their extra hours are part of their salary.
 */
export function isNurseSalariedForShift(getNurseById: (id: number) => Nurse | undefined, nurseId: number, shiftStart: DateTime | undefined): boolean {
	const nurse = getNurseById(nurseId);
	if (!nurse || !shiftStart) return false;
	return getBaseWageByTime(nurse, shiftStart)?.wageType === WageType.Annually;
}

/**
 * No punch for this long means we stop expecting punches. Product picked 3 months.
 * Not `nurse_inactive_after_days` (90) — that means "has this person left?", not "do they clock in?".
 */
export const NON_PUNCHING_STAFF_MONTHS = 3;

/**
 * Should this nurse clock in for a shift on `shiftStart`? No if either is true: they are on an annual
 * salary that day, or they have no punch here within `NON_PUNCHING_STAFF_MONTHS` (never punched counts).
 *
 * Two known trade-offs: substitutions don't reach PPD/HPPD (that reads raw `nursesActual`), and since
 * we store only the newest punch, a nurse's first punch makes older days expect punches again.
 */
export function isNursePunchExpected(getNurseById: (id: number) => Nurse | undefined, nurseId: number, shiftStart: DateTime | undefined): boolean {
	const nurse = getNurseById(nurseId);
	// No nurse or no shift date means no reason to hide a flag.
	if (!nurse || !shiftStart) return true;
	// Agency staff are skipped: most never punched here, and counting their schedule as worked would
	// inflate billed agency hours. `computePunchSummary` already has an Agency bucket for their gaps.
	if (isAgencyNurse(nurse.employmentType)) return true;
	if (getBaseWageByTime(nurse, shiftStart)?.wageType === WageType.Annually) return false;
	// Without punch history we can't tell "never punches" from "we didn't look", so flag it.
	if (!nurse.punchHistoryLoaded) return true;
	if (!nurse.lastPunchTime) return false;
	return nurse.lastPunchTime >= shiftStart.minus({ months: NON_PUNCHING_STAFF_MONTHS });
}

/**
 * Check if wage breakdown data is available for a nurse at a given time.
 * Returns false for annually-paid or PPD-excluded nurses.
 *
 * Nurses with no base wages (or all wages = 0) are allowed through when they
 * have a role — the backend estimates their wage using the department's
 * role/job average (DAT-189) and writes breakdown rows for them.
 *
 * When job management is enabled, PPD exclusion is resolved at the shift/job
 * level (`shiftExcludeFromPpd`) rather than the nurse level (`nurse.excludeFromPpd`).
 * Pass `options.jobManagementEnabled` and `options.shiftExcludeFromPpd` to get
 * the correct result in that mode.
 */
export function isWageBreakdownAvailable(
	nurse: Nurse | undefined,
	dateTime: DateTime,
	options: { jobManagementEnabled: boolean; shiftExcludeFromPpd: boolean | undefined },
): boolean {
	if (!nurse) return false;
	if (isShiftExcludedFromPpd(options.jobManagementEnabled, options.shiftExcludeFromPpd, nurse.excludeFromPpd)) return false;

	if (!nurse.baseWages?.length) {
		// No base wages configured — show breakdown only if the backend can estimate
		// (nurse has a role and is not on an annual salary).
		return !!nurse.role && nurse.wageType !== WageType.Annually;
	}

	const baseWage = getBaseWageByTime(nurse, dateTime) ?? nurse.baseWages.find((bw) => bw.wage > 0);
	if (!baseWage) {
		// Base wages exist but all are 0 — same rule: allow if nurse has a role.
		return !!nurse.role && nurse.baseWages[0].wageType !== WageType.Annually;
	}
	return baseWage.wageType !== WageType.Annually;
}

/**
 * DAT-906 · Whether a shift's row can open its wage breakdown panel: the conditions the day schedule's
 * table bodies put on the row's arrow and on rendering the panel, apart from the Show-wage reveal.
 */
export const canOpenShiftWageBreakdown = (shift: NurseInShift, nurse: Nurse | undefined, jobManagementEnabled: boolean): boolean =>
	!!shift.startTime &&
	!!shift.endTime &&
	!!shift.cost &&
	isWageBreakdownAvailable(nurse, shift.startTime, { jobManagementEnabled, shiftExcludeFromPpd: shift.excludeFromPpd });
